import cn from "classnames";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import ReactModal from "react-modal";
import { getExchangeRateV4 } from "../../api/IrsClient";
import { EXCHANGE_RATE_TYPE, MAX_MOBILE_NUMBER_LENGHT } from "../../constant";

const defaultLabel = { value: "", label: "-" };

const defaultFormValue = {
  mobile_number: "",
  cambodia_special_rate: defaultLabel.value,
  myanmar_special_rate: defaultLabel.value
}

function SpecialCustomerModal({
  data,
  isOpen,
  onOk,
  onCancel,
  header,
  textOk = "Submit",
  textCancel = "Cancel",
  loading = false,
  width = undefined
}) {
  const [cambodiaLabel, setCambodiaLabel] = useState([defaultLabel]);
  const [myanmarLabel, setMyanmarLabel] = useState([defaultLabel]);
  const [isDisableMobileInput, setIsDisableMobileInput] = useState(false);

  const {
    reset,
    register,
    setValue,
    handleSubmit,
    formState: { errors, isValid }
  } = useForm({
    defaultValues: defaultFormValue,
    mode: "onChange"
  });

  // init
  useEffect(() => {
    getSpecialCustomerExchangeRates();
  }, []);

  useEffect(() => {
    if (data && isOpen) {
      setValue("mobile_number", data.mobile_number);
      setValue("cambodia_special_rate", data.cambodia_special_rate?.id || defaultLabel.value);
      setValue("myanmar_special_rate", data.myanmar_special_rate?.id || defaultLabel.value);
      setIsDisableMobileInput(true);
    } else {
      reset();
    }
  }, [data, isOpen, cambodiaLabel, myanmarLabel]);

  function handleOnChangeMobileNumber(e) {
    const value = e.target.value.replace(/\D/g, "").slice(0, MAX_MOBILE_NUMBER_LENGHT);
    e.target.value = value;
  }

  function handleOnSubmit(formValue) {
    onOk(formValue);
  }

  function handleOnCancel() {
    reset();
    onCancel();
  }

  /**
   * Fetches special customer exchange rates and updates the labels for Myanmar and Cambodia.
   * 
   * @async
   * @function getSpecialCustomerExchangeRates
   * @returns {Promise<void>} A promise that resolves when the exchange rates have been fetched and labels updated.
   * @throws Will log an error to the console if the request fails.
   */
  async function getSpecialCustomerExchangeRates() {
    try {
      const request = {
        is_only_enable: true,
        type: "SPECIAL_CUSTOMER"
      };
      const { data: { status, data } } = await getExchangeRateV4(request);
      if (status.code === "0") {
        const myanmarRates = data.exchange_rates.filter(rate => rate.country_code === "95");
        const cambodiaRates = data.exchange_rates.filter(rate => rate.country_code === "855");
        setMyanmarLabel([defaultLabel, ...mapExchangeRateToOptions(myanmarRates)]);
        setCambodiaLabel([defaultLabel, ...mapExchangeRateToOptions(cambodiaRates)]);
      }
    } catch (error) {
      console.error(error);
    }
  }

  function mapExchangeRateToOptions(exchangeRates) {
    if (exchangeRates) {
      return exchangeRates.map(rate => ({ label: rate.label, value: rate.exchange_rate_id }));
    } else {
      return []
    }
  }

  function mapOptions(options) {
    return options.map(o => (
      <option key={o.value} value={o.value}>{o.label}</option>
    ));
  }

  return (
    <ReactModal
      isOpen={isOpen}
      ariaHideApp={false}
      className="custom-modal"
      style={{
        overlay: {
          zIndex: "5000",
        },
      }}
    >
      <div className="exchange-rate-modal" style={{ 'width': width || 'unset' }}>
        <form onSubmit={handleSubmit(handleOnSubmit)} autoComplete="off">
          <div className="custom-modal-content">
            <h5 className="custom-modal-message text-center p-3">{header}</h5>
            <div className="custom-modal-description text-center">

              <div className="form-group row">
                <label htmlFor="country" className="col-sm-5 col-form-label">Mobile No </label>
                <div className={cn("col-sm-7", {
                  "form-error": errors.mobile_number
                })}>
                  <input
                    className="form-control"
                    data-testid="modal-mobile-number-input"
                    type="text"
                    {...register("mobile_number", {
                      required: true,
                      minLength: {
                        value: MAX_MOBILE_NUMBER_LENGHT,
                        message: `Mobile number must be ${MAX_MOBILE_NUMBER_LENGHT} digits long.`
                      },
                      onChange: handleOnChangeMobileNumber
                    })}
                    disabled={isDisableMobileInput}
                  />
                  {errors.mobile_number && (
                    <span className="input-error-message">
                      {errors.mobile_number.message}
                    </span>
                  )}
                </div>
              </div>
              <div className="form-group row">
                <label htmlFor="currency" className="col-sm-5 col-form-label">Cambodia / KHR </label>
                <div className="col-sm-7">
                  <select
                    className="form-control"
                    data-testid="modal-cambodia-label-select"
                    {...register("cambodia_special_rate")}
                  >
                    {mapOptions(cambodiaLabel)}
                  </select>
                </div>
              </div>
              <div className="form-group row">
                <label htmlFor="name" className="col-sm-5 col-form-label">Myanmar / MMK </label>
                <div className="col-sm-7">
                  <select
                    className="form-control"
                    data-testid="modal-myanmar-label-select"
                    {...register("myanmar_special_rate")}
                  >
                    {mapOptions(myanmarLabel)}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* modal action */}
          <div className="custom-modal-action">
            <button
              className="btn btn-secondary"
              data-testid="modal-cancel-button"
              type="button"
              onClick={handleOnCancel}
              disabled={loading}
            >
              {textCancel}
            </button>
            <button
              className="btn btn-warning"
              data-testid="modal-submit-button"
              type="submit"
              disabled={loading || !isValid}
            >
              {textOk}
            </button>
          </div>
        </form>
      </div>
    </ReactModal>
  );
};

export default SpecialCustomerModal;
